<script setup>
  import { ref, computed, watchEffect, onMounted } from 'vue';
  import { useApiWindAlerts } from '@/composables/useApiWindAlerts';
  import WindAlertHistoryList from './WindAlertHistoryList.vue';
  import LoaderCircle from './LoaderCircle.vue';
  import { normalizeAlertRecord } from '@/helpers/alertHistory';

  const apiWindAlerts = useApiWindAlerts();

  const props = defineProps({
    alertId: {
      type: [String, Number],
      default: null
    }
  });

  const isLoading = ref(false);
  const error = ref(null);
  const allAlerts = ref([]);

  const filteredAlerts = computed(() => {
    if (props.alertId == null) return [];
    const target = String(props.alertId);
    return allAlerts.value.filter(a => {
      const candidates = [a.alert_id, a.id].filter(v => v != null).map(String);
      return candidates.includes(target);
    });
  });

  async function loadHistory() {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await apiWindAlerts.getAlertHistory();
      const data = response?.data;
      const reqError = response?.error;
      if (!data || reqError?.value) {
        error.value = reqError?.value || new Error('Failed to load alert history');
        allAlerts.value = [];
      } else {
        const value = data.value;
        const records = Array.isArray(value)
          ? value
          : value?.alert_records || value?.alerts || value?.alert_history || [];
        allAlerts.value = records.map(normalizeAlertRecord);
      }
    } catch (e) {
      error.value = e;
      allAlerts.value = [];
    } finally {
      isLoading.value = false;
    }
  }

  onMounted(() => {
    watchEffect(() => {
      if (props.alertId != null) loadHistory();
    });
  });
</script>

<template>
  <div>
    <div v-if="isLoading" class="jwx-grid jwx-place-content-center jwx-p-jumbo">
      <LoaderCircle :showText="false" />
    </div>

    <div v-else-if="error" class="jwx-modal__section">
      <div class="jwx-modal__section-body jwx-text-center">
        <p class="jwx-text jwx-m-bottom-lg">Unable to load alert history.</p>
        <button type="button" class="jwx-button jwx-button--pill" @click="loadHistory">Retry</button>
      </div>
    </div>

    <div v-else-if="filteredAlerts.length === 0" class="jwx-modal__section">
      <div class="jwx-modal__section-body">
        <p class="jwx-text jwx-text-center jwx-p-xxl">No recent alerts for this trigger.</p>
      </div>
    </div>

    <WindAlertHistoryList
      v-else
      :alerts="filteredAlerts"
      :groupBySpot="false"
      :showSpotName="false"
    />
  </div>
</template>
